'use client';
import { useContext, useEffect, useMemo } from 'react';
import useSWR from 'swr';

import { useI18nLn } from '~/hooks/store';
import {
  type FetchGachaIdInfoByUniqueNameParams,
  fetchGachaIdInfoByUniqueName,
} from '~/src/services/arcApi';
import { isArcApiError } from '~/src/services/errors';
import { isActiveToken } from '~/src/services/users';
import { RootContext } from '~/store';
import { GachaDetail } from '~/types/resources/gachaDetail';

type UseGachaIdInfo = {
  gachaInfo?: GachaDetail;
  statusCode: number | undefined;
  loading: boolean;
  error: Error;
  mutate: () => void;
};

export const useGachaIdInfo = (
  unique_name: string,
  option?: {
    initialState?: GachaDetail;
  }
): UseGachaIdInfo => {
  const { i18nLn } = useI18nLn();
  const { token, loggedInState } = useContext(RootContext);

  /**
   * ユーザ情報がローディング中はそもそもフェッチしない
   * ユーザログイン状態が確定後はトークンの付与によってフェッチする
   * フェッチしない場合はnullを返す
   */
  const fetchParams = useMemo((): FetchGachaIdInfoByUniqueNameParams | null => {
    if (loggedInState === 'loading') return null;

    if (loggedInState === 'loggedIn') {
      if (unique_name && token && isActiveToken(token)) {
        return [unique_name, token.token, i18nLn];
      }
      // ログイン状態で有効なトークンが存在しない場合にはフェッチしない
      return null;
    } else {
      // 未ログイン時はトークンを付与せずフェッチ関数を実行
      return [unique_name, null, i18nLn];
    }
  }, [token, unique_name, i18nLn, loggedInState]);

  const { data, error, mutate } = useSWR(
    fetchParams,
    fetchGachaIdInfoByUniqueName,
    {
      dedupingInterval: 1000 * 60 * 5,
      revalidateIfStale: false,
      keepPreviousData: true,
      fallbackDate: option?.initialState,
    }
  );

  useEffect(() => {
    if (unique_name == null) return;
    mutate();
  }, [unique_name, i18nLn, token]);

  const is_fetch_error: boolean = useMemo(() => {
    return isArcApiError(data);
  }, [data]);

  return {
    gachaInfo: data?.statusCode == 200 ? data.gachaDetail : undefined,
    statusCode: data?.statusCode ?? undefined,
    loading: !data && !error,
    error: is_fetch_error ? data : error,
    mutate,
  };
};
